import { type ReactNode, useEffect, useRef } from "react";
import {
  Autocomplete as MUIAutocomplete,
  type AutocompleteProps as MUIAutocompleteProps,
  TextField as MUITextField,
} from "@mui/material";
import { C_PRIMARY, C_PRIMARY_HOVER } from "../icon/Colors";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { makeStyles } from "@mui/styles";
import { type ColorVariants, type TextFieldVariants } from "~/common/types";

export interface AutocompleteProps<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> extends Omit<MUIAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>, "renderInput"> {
  label?: ReactNode;
  helperText?: ReactNode;
  error?: boolean;
  "data-test-id"?: string;
  autoFocus?: boolean;
  placeholder?: string;
  startAdornment?: ReactNode;
  inputProps?: object;
  variant?: TextFieldVariants;
  color?: ColorVariants;
  shrink?: boolean;
}

const useStyles = makeStyles({
  option: {
    padding: 0,
    background: "none !important",
  },
  endAdornment: {
    "& .scha-icon-chevron-down": {
      color: C_PRIMARY,
      "&:hover": {
        color: C_PRIMARY_HOVER,
        // TODO - COLORS M3 - What about hover?
      },
    },
  },
  paper: {},
  listbox: {},
});

export const Autocomplete = <
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>({
  label,
  helperText,
  error,
  // "data-test-id": testId,
  autoFocus,
  placeholder,
  startAdornment,
  inputProps,
  variant,
  color,
  shrink,
  ...rest
}: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>) => {
  const classes = useStyles(rest);

  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      const el = inputRef.current;
      window.setTimeout(() => el?.focus(), 16);
    }
  }, []);

  return (
    <MUIAutocomplete
      {...rest}
      classes={classes}
      renderInput={(params) => (
        <MUITextField
          inputRef={(ref) => {
            if (autoFocus) {
              inputRef.current = ref;
            }
          }}
          {...params}
          variant={variant}
          color={color}
          label={label}
          helperText={helperText}
          error={error}
          autoComplete={"off"}
          placeholder={placeholder}
          slotProps={{
            input: { ...params.InputProps, startAdornment: startAdornment },
            htmlInput: { ...params.inputProps, ...inputProps },
            inputLabel: { shrink },
          }}
        />
      )}
      popupIcon={<ArrowDropDownIcon />}
    />
  );
};
